// cartSlice.js
import AsyncCart from '../../api/cart/AsyncCart';

export const createCartSlice = (set, get) => {
  // Create an instance of AsyncCart
  const cart = new AsyncCart({}, console);
  // Helper function to get category summary with custom logic
  const getCategorySummaryWithLogic = () => {
    const summary = cart.getCategorySummary();

    // Fix addon grouping: AsyncCart groups by category, we need to group by addon_name
    Object.keys(summary).forEach(categoryName => {
      const categoryData = summary[categoryName];
      const regroupedAddons = {};

      // Process each item to regroup addons by addon_name
      categoryData.items?.forEach(item => {
        item.addons?.forEach(addon => {
          const addonName = addon.addon_name || 'Addon';

          if (!regroupedAddons[addonName]) {
            regroupedAddons[addonName] = {
              text: addonName,
              original_price: 0,
              discounted_price: 0,
              quantity: 0,
              subtotal: 0
            };
          }

          regroupedAddons[addonName].original_price += addon.base_price || 0;
          regroupedAddons[addonName].discounted_price += addon.price_with_discount || 0;
          regroupedAddons[addonName].quantity += addon.quantity || 0;
          regroupedAddons[addonName].subtotal += addon.subtotal || 0;
        });
      });

      // Replace the category's addons with regrouped addons
      categoryData.addons = regroupedAddons;
    });

    // Custom logic: Move "Dryer Vent Cleaning" under "Air Duct Cleaning" as an addon
    if (summary["Dryer Vent Cleaning"] !== undefined) {
      if (summary["Air Duct Cleaning"] === undefined) {
        summary["Air Duct Cleaning"] = {
          text: "Air Duct Cleaning",
          original_price: 0,
          discounted_price: 0,
          quantity: 0,
          subtotal: 0,
          addons: {},
          items: [],
        };
      }

      summary["Air Duct Cleaning"].addons["Dryer Vent"] = {
        text: "Dryer Vent",
        original_price: summary["Dryer Vent Cleaning"].original_price,
        discounted_price: summary["Dryer Vent Cleaning"].discounted_price,
        quantity: summary["Dryer Vent Cleaning"].quantity,
        subtotal: summary["Dryer Vent Cleaning"].subtotal,
      };
      delete summary["Dryer Vent Cleaning"];
    }

    return summary;
  };

  // Helper function to sync cart summary from AsyncCart to Zustand store
  // We need syncFromCart() because React components are watching the Zustand state, not the AsyncCart instance directly.
  const syncFromCart = (setUpdating = false) => {
    const summary = cart.getCartSummary();
    const categorySummary = getCategorySummaryWithLogic();

    // Get current franchise data to calculate service charge
    const state = get();
    const franchiseData = state.currentFranchise;
    const usesPricing = franchiseData?.uses_pricing !== false; // Default to true if undefined

    // For pricing franchises, use appliedDiscounts (discounts with calculated amounts)
    // For non-pricing franchises, use discounts (all validated discounts)
    const discountsForPromotion = usesPricing ? summary.appliedDiscounts : summary.discounts;
    const promotionCodes = discountsForPromotion.map(discount => discount.code || discount.promo_code || discount.id);

    const serviceCharge = parseFloat(
      franchiseData?.prices?.field_additional_charge ||
      franchiseData?.prices?.field_service_charge ||
      0
    );

    set(state => ({
      cart: {
        ...state.cart,
        items: summary.items,
        itemCount: summary.itemCount,
        discounts: summary.discounts,
        appliedDiscounts: summary.appliedDiscounts,
        subtotal: summary.subtotal,
        actualSubtotal: summary.actual_subtotal,
        baseSubtotal: summary.base_subtotal,
        taxRate: summary.tax,
        taxes: summary.taxes,
        total: summary.total,
        minimumAmount: summary.minimumAmount,
        minimumAmountMet: summary.minimumAmountMet,
        minimumAmountNeeded: summary.minimumAmountNeeded,
        totalSavings: summary.totalSavings,
        categorySummary: categorySummary,
        serviceCharge: serviceCharge,
        isUpdating: setUpdating
      },
      // Sync applied promotions from cart to promotion slice
      // Use code (promo_code) as the identifier to match with promotions
      // Clear applied promotions when cart is empty
      appliedPromotions: summary.itemCount === 0 ? [] : promotionCodes
    }));
  };

  // initial snapshot of cart summary to populate Zustand state
  const initial = cart.getCartSummary();
  const initialCategorySummary = getCategorySummaryWithLogic();

  return {
    cart: {
      // raw instance of cart
      instance: cart,
      items: initial.items,
      itemCount: initial.itemCount,
      discounts: initial.discounts,
      appliedDiscounts: initial.appliedDiscounts,
      subtotal: initial.subtotal,
      actualSubtotal: initial.actual_subtotal,
      baseSubtotal: initial.base_subtotal,
      taxRate: initial.tax,
      taxes: initial.taxes,
      total: initial.total,
      minimumAmount: initial.minimumAmount,
      minimumAmountMet: initial.minimumAmountMet,
      minimumAmountNeeded: initial.minimumAmountNeeded,
      totalSavings: initial.totalSavings,
      categorySummary: initialCategorySummary,
      serviceCharge: 0,
      isUpdating: false,
    },

    // Add item to cart
    addCartItem: async item => {
      // Set isUpdating to true before the async operation
      set(state => ({ cart: { ...state.cart, isUpdating: true } }));
      // Add item to the AsyncCart instance
      await cart.addItem(item);
      // Sync cart state and set isUpdating to false
      syncFromCart(false);
    },

    // Set cart items (replace all items in the cart)
    setCartItems: async items => {
      set(state => ({ cart: { ...state.cart, isUpdating: true } }));

      // Get current franchise data to re-apply tax and minimum after setItems
      const state = get();
      const franchiseData = state.currentFranchise;

      await cart.setItems(items);

      // Re-apply tax and minimum amount after setItems (in case they were cleared)
      if (franchiseData) {
        const taxRate = parseFloat(franchiseData?.prices?.field_tax || 0);
        const minimumAmount = parseFloat(franchiseData?.prices?.field_minimum_charge || 0);
        cart.setTax(taxRate);
        cart.setMinimumAmount(minimumAmount);
        // Recalculate totals after setting tax rate to ensure taxes are calculated
        await cart.updateCartTotals();
      }

      // Sync cart state and set isUpdating to false
      syncFromCart(false);
    },


    // Reset cart
    resetCart: () => {
      cart.resetCart();
      syncFromCart();
    },

    // Initialize cart with franchise settings (tax, minimum amount)
    initializeCart: async (franchiseData) => {
      // Store tax as percentage (e.g., 8.25) - AsyncCart.calculateTax() will divide by 100
      const taxRate = parseFloat(franchiseData?.prices?.field_tax || 0);
      const minimumAmount = parseFloat(franchiseData?.prices?.field_minimum_charge || 0);

      cart.setTax(taxRate);
      cart.setMinimumAmount(minimumAmount);
      // Recalculate totals after setting tax rate to ensure taxes are calculated
      await cart.updateCartTotals();

      // On load this instance is empty and would overwrite the restored cart.
      if (cart.getCartSummary().itemCount > 0) {
        syncFromCart();
      }
    },

    // Apply discounts to cart (exposed for use by other slices)
    applyDiscounts: async (discounts) => {
      set(state => ({ cart: { ...state.cart, isUpdating: true } }));

      if (cart && typeof cart.setDiscounts === 'function') {
        await cart.setDiscounts(discounts);

        // Workaround: setDiscounts() doesn't actually trigger updateCartTotals due to
        // _isUpdating flag conflict, so we call it explicitly here after setDiscounts completes
        await cart.updateCartTotals();

        // Sync cart state and set isUpdating to false
        syncFromCart(false);
      } else {
        set(state => ({ cart: { ...state.cart, isUpdating: false } }));
      }
    },

    // Combined method: Set cart items AND apply discounts in a single batch
    // This prevents multiple renders when syncing cart with configuration
    setCartItemsWithDiscounts: async (items, discounts) => {
      set(state => ({ cart: { ...state.cart, isUpdating: true } }));

      // Get current franchise data
      const state = get();
      const franchiseData = state.currentFranchise;

      // Step 1: Set service configuration items (clears cart first)
      await cart.setItems(items);

      // Step 2: Re-apply tax and minimum amount after setItems
      if (franchiseData) {
        const taxRate = parseFloat(franchiseData?.prices?.field_tax || 0);
        const minimumAmount = parseFloat(franchiseData?.prices?.field_minimum_charge || 0);
        cart.setTax(taxRate);
        cart.setMinimumAmount(minimumAmount);
        // Recalculate totals after setting tax rate to ensure taxes are calculated
        await cart.updateCartTotals();
      }

      // Step 3: Add Service Charge using addItem (matches old CartHelper pattern)
      // Check field_additional_charge first, then field_service_charge (matches CartHelper.js line 43)
      const serviceCharge = parseFloat(
        franchiseData?.prices?.field_additional_charge ||
        franchiseData?.prices?.field_service_charge ||
        0
      );

      if (serviceCharge > 0 && !cart.itemAdded('service_charge')) {
        await cart.addItem({
          item_id: 'service_charge',
          label: 'Service Charge',
          category: 'Service Charge',
          category_id: 'service_charge',
          service_term: 'service_charge',
          price_per_unit: serviceCharge,
          quantity: 1,
          taxable: false,
          discountable: false,
          exclude_from_min: true,
          addons: []
        });
      }

      // Step 4: Add Portable Equipment using addItem (matches old CartHelper pattern)
      const needsPortableEquipment = state.needsPortableEquipment;
      const portableCharge = parseFloat(franchiseData?.prices?.field_portable_charge || 0);

      if (needsPortableEquipment && portableCharge > 0) {
        if (cart.itemAdded('portable_equipment')) {
          // Update quantity if already exists (though it should always be 1)
          await cart.updateItem('portable_equipment', { quantity: 1 });
        } else {
          await cart.addItem({
            item_id: 'portable_equipment',
            label: 'Portable Equipment',
            category: 'Equipment',
            category_id: 'equipment',
            service_term: 'equipment',
            price_per_unit: portableCharge,
            quantity: 1,
            taxable: true,
            discountable: false,
            exclude_from_min: true,
            addons: []
          });
        }
      } else if (!needsPortableEquipment && cart.itemAdded('portable_equipment')) {
        // Remove if portable equipment is unchecked
        await cart.removeItem('portable_equipment');
      }

      // Step 5: Apply discounts
      if (cart && typeof cart.setDiscounts === 'function' && discounts) {
        await cart.setDiscounts(discounts);
        await cart.updateCartTotals();
      }

      // Single sync at the end - this batches all updates into ONE render
      syncFromCart(false);
    },

  };
};
